import React from 'react';
import moment from 'moment';
import './PrintOrder.css';
import { formatMoney, numberToWords } from '@app/utils/utils';
import { OrderModel } from '@app/interfaces/order';
import { PhoneFilled } from '@ant-design/icons';
import { CiGlobe, CiLocationOn } from 'react-icons/ci';
import { DATE_FORMAT } from '@app/constants/dateFormats';

export type QrType = 'vietqr' | 'onepay';

interface OrderProps {
  record: OrderModel;
  qrCodeUrl?: string;
  qrType?: QrType;
  logoUrl?: string;
  storeName?: string;
  storeAddress?: string;
  storePhone?: string;
  storeWebsite?: string;
  /** Ảnh QR (data URL) của đơn — quét để mở trang xử lý nhanh. */
  orderQrUrl?: string;
}

const ItemTable = ({ record }: { record: OrderModel }) => {
  const items = record.items || [];

  return (
    <>
      <table className="print-table">
        <colgroup>
          <col className="col-stt" />
          <col className="col-name" />
          <col className="col-unit" />
          <col className="col-qty" />
          <col className="col-price" />
          <col className="col-total" />
        </colgroup>
        <thead>
          <tr>
            <th className="text-center">STT</th>
            <th>Tên hàng</th>
            <th className="text-center">ĐVT</th>
            <th className="text-center">SL</th>
            <th className="text-right">Đơn giá</th>
            <th className="text-right">Thành tiền</th>
          </tr>
        </thead>
        <tbody>
          {items.length > 0 ? (
            items.map((item: any, index: number) => (
              <tr key={index}>
                <td className="text-center">{index + 1}</td>
                <td>{item.name}</td>
                <td className="text-center">{item.unit}</td>
                <td className="text-center">{item.qty}</td>
                <td className="text-right">{formatMoney(Number(item.price))}</td>
                <td className="text-right">{formatMoney(Number(item.amount))}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={6} className="print-table__empty-cell">
                Không có sản phẩm
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="invoice-summary">
        <div className="invoice-summary__row">
          <span className="invoice-summary__label">Tổng giá trị</span>
          <span className="invoice-summary__value">{formatMoney(record.before_discount_amount)}</span>
        </div>
        <div className="invoice-summary__row">
          <span className="invoice-summary__label">Chiết khấu ({record.discount_percent}%)</span>
          <span className="invoice-summary__value">{formatMoney(record.discount_amount)}</span>
        </div>
        <div className="invoice-summary__row">
          <span className="invoice-summary__label">VAT ({record.vat_percent}%)</span>
          <span className="invoice-summary__value">{formatMoney(record.vat_amount)}</span>
        </div>
        {Number(record.shipping_fee) > 0 && (
          <div className="invoice-summary__row">
            <span className="invoice-summary__label">Phí ship</span>
            <span className="invoice-summary__value">{formatMoney(record.shipping_fee)}</span>
          </div>
        )}
        {Number(record.other_amount) > 0 && (
          <div className="invoice-summary__row">
            <span className="invoice-summary__label">Chi phí khác</span>
            <span className="invoice-summary__value">{formatMoney(Number(record.other_amount))}</span>
          </div>
        )}
        {Number(record.points_discount_amount) > 0 && (
          <div className="invoice-summary__row">
            <span className="invoice-summary__label">Trừ điểm tích luỹ ({record.points_used} điểm)</span>
            <span className="invoice-summary__value">-{formatMoney(Number(record.points_discount_amount))}</span>
          </div>
        )}
        <div className="invoice-summary__row invoice-summary__row--total">
          <span className="invoice-summary__label">TỔNG CỘNG</span>
          <span className="invoice-summary__value">{formatMoney(record.total_amount)}</span>
        </div>
      </div>

      <div className="amount-words-line">
        Bằng chữ: <strong>{numberToWords(record.total_amount)}.</strong>
      </div>
    </>
  );
};

const QR_LABELS: Record<QrType, string> = {
  vietqr: 'Quét VietQR để thanh toán',
  onepay: 'Quét mã QR bằng app ngân hàng để thanh toán',
};

// Ô QR đơn trong header chỉ rộng 88px: đơn gộp nhiều mã thì chỉ ghi vài mã đầu, mỗi mã một dòng.
const MAX_QR_CODES = 2;

const PrintOrder: React.FC<OrderProps> = ({
  record,
  qrCodeUrl,
  qrType = 'vietqr',
  logoUrl = '',
  storeName = '',
  storeAddress = '',
  storePhone = '',
  storeWebsite = '',
  orderQrUrl,
}) => {
  const [datePart] = record.created_date.split(' ');
  const [day, month, year] = datePart.split('-').map(Number);
  const orderCodes: string[] = Array.isArray(record.order_code) ? record.order_code : [record.order_code];
  const orderCode = orderCodes.join('+');
  const qrCodes = orderCodes.slice(0, MAX_QR_CODES);
  const hiddenQrCodes = orderCodes.length - qrCodes.length;

  return (
    <div className="container">
      {logoUrl && <img src={logoUrl} alt="" className="invoice-watermark" aria-hidden="true" />}
      <div className="store-header">
        {logoUrl && (
          <div className="store-header__brand">
            <img src={logoUrl} alt={storeName} className="store-header__logo" />
          </div>
        )}
        <div className="store-header__info">
          <div className="store-header__name">{storeName}</div>
          {storeAddress && (
            <div className="store-header__row">
              <CiLocationOn /> {storeAddress}
            </div>
          )}
          {storePhone && (
            <div className="store-header__row">
              <PhoneFilled /> {storePhone}
            </div>
          )}
          {storeWebsite && (
            <div className="store-header__row">
              <CiGlobe /> {storeWebsite}
            </div>
          )}
        </div>

        {/* QR của đơn: đặt cạnh logo để shipper quét ngay khi cầm phiếu, tách hẳn khỏi
            QR thanh toán ở cuối phiếu nên không bị nhầm hai mã với nhau. */}
        {orderQrUrl && (
          <div className="order-qr">
            <img src={orderQrUrl} alt={`Mã QR đơn ${orderCodes[0]}`} className="order-qr__img" />
            <div className="order-qr__label">Quét cập nhật đơn</div>
            {qrCodes.map((code) => (
              <div key={code} className="order-qr__code">
                {code}
              </div>
            ))}
            {hiddenQrCodes > 0 && <div className="order-qr__more">+{hiddenQrCodes} đơn</div>}
          </div>
        )}
      </div>

      <div className="voucher-title">
        <h1>Phiếu bán hàng</h1>
      </div>
      <div className="voucher-meta">
        <span>
          Số: <strong>{orderCode}</strong>
        </span>
        <span>
          Ngày: <strong>{moment(record.trans_date).format(DATE_FORMAT)}</strong>
        </span>
      </div>

      <div className="field-row">
        <span className="label">Khách hàng</span>
        <span className="value">{record.customer?.name}</span>
      </div>
      <div className="field-row">
        <span className="label">Địa chỉ giao hàng</span>
        <span className="value">{record.delivery_address}</span>
      </div>
      <div className="field-row">
        <span className="label">Phương thức thanh toán</span>
        <span className="value">{record.payment_type_name}</span>
      </div>
      {record.remark && (
        <div className="field-row">
          <span className="label">Ghi chú</span>
          <span className="value text-danger">{record.remark}</span>
        </div>
      )}

      <ItemTable record={record} />

      <div className="signature">
        <div className="signature-block">
          {qrCodeUrl ? (
            <>
              <div className="print-qr-title">THANH TOÁN</div>
              <img src={qrCodeUrl} alt="QR thanh toán" width={150} className="print-qr-img" />
              <div className={`print-qr-label print-qr-label--${qrType}`}>{QR_LABELS[qrType]}</div>
              {qrType === 'onepay' && (
                <div className="print-qr-note" style={{ fontSize: 9, color: '#8c8c8c', marginTop: 2 }}>
                  Mã QR có hiệu lực trong ngày. Nếu hết hạn, liên hệ shop để nhận mã mới.
                </div>
              )}
            </>
          ) : (
            <div className="print-qr-placeholder">QR Code</div>
          )}
        </div>
        <div className="signature-block">
          <div className="signature-block__title">NGƯỜI NHẬN HÀNG</div>
          <div className="signature-block__note">
            Ngày {day} tháng {month} năm {year}
            <br />
            (Ký, ghi rõ họ tên)
          </div>
          <div className="signature-block__name"></div>
        </div>
        <div className="signature-block">
          <div className="signature-block__title">NGƯỜI LẬP HÓA ĐƠN</div>
          <div className="signature-block__note">(Ký, ghi rõ họ tên)</div>
          <div className="signature-block__name">{record.created_by}</div>
        </div>
      </div>
    </div>
  );
};

export default PrintOrder;
